import round from 'lodash/round';

import { CURRENCIES } from '@savgroup-front-common/constants';
import { RecipientType } from '@savgroup-front-common/types';

import { useGetSellerById } from '../../../../../domains/sellers/useGetSellerById';
import { ReverseMoneyHelper } from '../../../../../helpers/ReverseMoneyHelper';
import { useGetOwnerDeductibleAndAlreadyPaidAmount } from '../../../../../hooks/useGetOwnerDeductibleAndAlreadyPaidAmount';
import { AdaptedQuotationNewModel } from '../../../types/AdaptedQuotationNewModel';
import { QuotationPendingCustomerValidationNewModel } from '../../QuotationPendingCustomerValidation.types';

interface UseCalculateQuotationTotalsArgs {
  quotation:
    | QuotationPendingCustomerValidationNewModel
    | AdaptedQuotationNewModel;
  fileId: string;
}

const VAT_TO_PRE_VAT_RATIO = 1.2;

export const useCalculateQuotationPendingCustomerValidationTotals = ({
  quotation,
  fileId,
}: UseCalculateQuotationTotalsArgs) => {
  const { alreadyPaidAmountInPreviousQuotation, deductibleAmountFromClaim } =
    useGetOwnerDeductibleAndAlreadyPaidAmount({ fileId });

  const totalAdministrativeFees = (quotation.administrativeFees || []).reduce(
    (acc, administrativeFee) => {
      if (administrativeFee.recipient?.recipientType !== RecipientType.OWNER) {
        return acc;
      }

      const newAmount = administrativeFee.preTaxAmount.amount;
      const newAmountIncludingTaxes =
        administrativeFee.amountIncludedTax.amount;

      return {
        totalPreVat: acc.totalPreVat + newAmount,
        totalVat: acc.totalVat + newAmountIncludingTaxes,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );
  const totalService = (quotation.services || []).reduce(
    (acc, service) => {
      if (service.recipient?.recipientType !== RecipientType.OWNER) {
        return acc;
      }

      const newAmount = service.preTaxAmount.amount;
      const newAmountIncludingTaxes = service.amountIncludedTax.amount;

      return {
        totalPreVat: acc.totalPreVat + newAmount,
        totalVat: acc.totalVat + newAmountIncludingTaxes,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );
  const totalUnknownService = (quotation.unknownServices || []).reduce(
    (acc, unknownService) => {
      if (unknownService.recipient?.recipientType !== RecipientType.OWNER) {
        return acc;
      }

      const newAmount = unknownService.preTaxAmount.amount;
      const newAmountIncludingTaxes = unknownService.amountIncludedTax.amount;

      return {
        totalPreVat: acc.totalPreVat + newAmount,
        totalVat: acc.totalVat + newAmountIncludingTaxes,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );
  const totalUnknownSparePart = (quotation.unknownSpareParts || []).reduce(
    (acc, unknownSparePart) => {
      if (unknownSparePart.recipient?.recipientType !== RecipientType.OWNER) {
        return acc;
      }

      const newAmount = unknownSparePart.preTaxAmount.amount;
      const newAmountIncludingTaxes = unknownSparePart.amountIncludedTax.amount;

      return {
        totalPreVat: acc.totalPreVat + newAmount,
        totalVat: acc.totalVat + newAmountIncludingTaxes,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );

  const totalSparePartLines = (quotation.sparePartLines || []).reduce(
    (acc, sparePartQuotationLine) => {
      if (sparePartQuotationLine.recipient?.data !== RecipientType.OWNER) {
        return acc;
      }

      if (!sparePartQuotationLine.isSelectedForRepair) {
        return acc;
      }

      const amountPreVat = round(
        sparePartQuotationLine.recommendedSalePriceIncludingTaxes.amount /
          VAT_TO_PRE_VAT_RATIO,
        2,
      );

      return {
        totalPreVat: acc.totalPreVat + amountPreVat,
        totalVat:
          acc.totalVat +
          sparePartQuotationLine.recommendedSalePriceIncludingTaxes.amount,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );

  const totalDiscount = (quotation.discounts || []).reduce(
    (acc, discount) => {
      const newAmount = discount.preTaxAmount.amount;
      const newAmountIncludingTaxes = discount.amountIncludedTax.amount;

      return {
        totalPreVat: acc.totalPreVat + newAmount,
        totalVat: acc.totalVat + newAmountIncludingTaxes,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );
  const totalAlreadyPaid = ReverseMoneyHelper.add(
    ReverseMoneyHelper.abs(alreadyPaidAmountInPreviousQuotation),
    ReverseMoneyHelper.abs(deductibleAmountFromClaim),
  );

  const bonus =
    quotation?.bonus && !quotation.isBonusRemoved
      ? quotation.bonus.amountIncludedTax
      : undefined;

  const subtotalPreVat =
    totalAdministrativeFees.totalPreVat +
    totalService.totalPreVat +
    totalUnknownService.totalPreVat +
    totalUnknownSparePart.totalPreVat +
    totalSparePartLines.totalPreVat;
  const subtotalVat =
    totalAdministrativeFees.totalVat +
    totalService.totalVat +
    totalUnknownService.totalVat +
    totalUnknownSparePart.totalVat +
    totalSparePartLines.totalVat;
  const totalAmountToBePaid = parseFloat(
    (
      subtotalVat -
      Math.abs(totalDiscount.totalVat) -
      totalAlreadyPaid.amount -
      Math.abs(bonus?.amount || 0)
    ).toFixed(2),
  );

  const seller = useGetSellerById({
    sellerId: quotation.sellerId,
  });

  const currency = seller?.currency || CURRENCIES.EUR;

  return {
    subtotalPreVat: { amount: subtotalPreVat, currency },
    subtotalVat: { amount: subtotalVat, currency },
    totalDiscountIncludedTax: quotation.totalDiscountIncludedTax,
    alreadyPaid: { amount: totalAlreadyPaid.amount, currency },
    totalAmountToBePaid: {
      amount: totalAmountToBePaid,
      currency,
    },
    bonus,
  };
};
